import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { RecommendationCategory } from '@lizrd/contracts';
import { getRecommendations } from '../../lib/api';
import { RECO_CATEGORY_META } from '../../lib/reco-categories';
import { CategoryIcon } from '../CategoryIcon';

/** A few representative categories to teach value while the sync is still scanning
 * (before any recommendation has been computed). Framed as examples, not results. */
const EXAMPLES: { key: keyof typeof RECO_CATEGORY_META; blurb: string }[] = [
  { key: 'idle', blurb: 'Resources running around the clock at near-zero utilization.' },
  { key: 'rightsize', blurb: 'Instances and databases provisioned larger than their real usage.' },
  { key: 'orphaned', blurb: 'Unattached disks, idle IPs, and load balancers still billing.' },
  { key: 'schedule', blurb: 'Non-production environments left on through nights and weekends.' },
  { key: 'commitment', blurb: 'Steady-state usage that’s cheaper committed than on-demand.' },
  { key: 'ai', blurb: 'Over-provisioned GPUs and inefficient LLM token usage.' },
];

/**
 * Shown beneath the live sync pipeline so the wait teaches value. While the sync is
 * still scanning it lists the kinds of savings Lizrd looks for; the moment the first
 * recommendations are computed (one batch at graph convergence), it flips to the
 * REAL categories found in this domain with a live count per type — the count tinted
 * in the category's own colour. A running teaser of the value waiting on the dashboard.
 * Keyed only by envId — reused by the onboarding Sync step and the add-domain modal.
 */
export function SyncValuePreview({ envId }: { envId: string }) {
  const recs = useQuery({
    queryKey: ['recommendations', envId],
    queryFn: () => getRecommendations(envId),
    // Poll while nothing is found yet (still scanning / computing); stop once results land.
    refetchInterval: (q) =>
      q.state.data && !q.state.data.computing && (q.state.data.items.length ?? 0) > 0
        ? false
        : 3000,
  });

  // Count open opportunities per category, biggest first.
  const found = useMemo(() => {
    const m = new Map<RecommendationCategory, number>();
    for (const r of recs.data?.items ?? []) {
      if (r.status === 'open') m.set(r.category, (m.get(r.category) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [recs.data]);

  const hasFound = found.length > 0;
  const total = found.reduce((s, [, n]) => s + n, 0);

  return (
    <div className="mt-5 rounded-md border border-hairline bg-surface-2/40 p-4">
      {hasFound ? (
        <div className="text-center">
          <div className="font-display text-4xl font-bold leading-none tracking-tight text-iris tnum">
            {total}
          </div>
          <div className="mt-2 font-mono text-[0.62rem] font-bold uppercase tracking-wider text-muted">
            opportunities found
          </div>
          <p className="mx-auto mt-2 max-w-md text-xs text-muted">
            and counting — the full list, with estimated savings, lands on your dashboard.
          </p>
        </div>
      ) : (
        <>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">
            While we work — the kinds of savings Lizrd looks for
          </p>
          <p className="mt-0.5 text-xs text-muted">
            Examples, not your results yet — your own findings appear as the sync completes.
          </p>
        </>
      )}

      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {hasFound
          ? found.map(([category, n]) => {
              const meta = RECO_CATEGORY_META[category];
              return (
                <div
                  key={category}
                  className="flex items-center gap-2.5 rounded-sm border border-hairline bg-surface px-3 py-2"
                >
                  <CategoryIcon category={category} className="h-5 w-5" tint />
                  <span className="min-w-0 flex-1 truncate text-xs font-semibold text-bone">
                    {meta.label}
                  </span>
                  {/* Count tinted in the category's own colour (per the headline). */}
                  <span
                    className="font-display text-sm font-bold tnum"
                    style={{ color: meta.color }}
                  >
                    {n}
                  </span>
                </div>
              );
            })
          : EXAMPLES.map(({ key, blurb }) => {
              const meta = RECO_CATEGORY_META[key];
              return (
                <div
                  key={key}
                  className="flex items-start gap-2.5 rounded-sm border border-hairline bg-surface px-3 py-2"
                >
                  <CategoryIcon category={key} className="h-5 w-5" tint />
                  <div className="min-w-0">
                    <div className="text-xs font-semibold" style={{ color: meta.color }}>
                      {meta.label}
                    </div>
                    <p className="mt-0.5 text-xs leading-snug text-muted">{blurb}</p>
                  </div>
                </div>
              );
            })}
      </div>
    </div>
  );
}
